
import SlotMap from '@neumatter/slot-map'
import Duration from './Duration.js'
import DateTime from './DateTime.js'

class DurationObserverEntry {
  constructor (type, name, startTime, duration) {
    this.entryType = type
    this.name = name
    this.startTime = startTime
    this.duration = duration
    Object.freeze(this)
  }
}

class DurationObserverMark extends DurationObserverEntry {
  constructor (name, startTime) {
    super('mark', name, startTime, new Duration())
  }
}

class DurationObserverMeasure extends DurationObserverEntry {
  constructor (name, startTime, duration) {
    super('measure', name, startTime, duration)
  }
}

/**
 *
 * @param {Duration} a
 * @param {Duration} b
 * @returns {boolean}
 */
function durationLessThanOrEqual (a, b) {
  if (a.sign !== b.sign) {
    return a.sign < b.sign
  }
  if (a.years !== b.years) {
    return a.years < b.years
  }
  if (a.months !== b.months) {
    return a.months < b.months
  }
  if (a.weeks !== b.weeks) {
    return a.weeks < b.weeks
  }
  if (a.days !== b.days) {
    return a.days < b.days
  }
  if (a.hours !== b.hours) {
    return a.hours < b.hours
  }
  if (a.minutes !== b.minutes) {
    return a.minutes < b.minutes
  }
  if (a.seconds !== b.seconds) {
    return a.seconds < b.seconds
  }
  if (a.milliseconds !== b.milliseconds) {
    return a.milliseconds < b.milliseconds
  }
  if (a.microseconds !== b.microseconds) {
    return a.microseconds < b.microseconds
  }
  if (a.nanoseconds !== b.nanoseconds) {
    return a.nanoseconds < b.nanoseconds
  }

  return true
}

class DurationObserverList {
  constructor () {
    /** @type {Map<string, DurationObserverMark>} */
    this[0] = new Map()
    /** @type {Map<string, DurationObserverMeasure>} */
    this[1] = new Map()
  }

  entries () {
    const response = []
    const markIterator = this[0].values()
    let markIteratorResult = markIterator.next()
    const measureIterator = this[1].values()
    let measureIteratorResult = measureIterator.next()

    while (!measureIteratorResult.done) {
      while (
        !markIteratorResult.done &&
        durationLessThanOrEqual(
          markIteratorResult.value.startTime,
          measureIteratorResult.value.startTime
        )
      ) {
        response.push(markIteratorResult.value)
        markIteratorResult = markIterator.next()
      }

      response.push(measureIteratorResult.value)
      measureIteratorResult = measureIterator.next()
    }

    while (!markIteratorResult.done) {
      response.push(markIteratorResult.value)
      markIteratorResult = markIterator.next()
    }

    return response
  }

  entriesByName (name, type) {
    if (type === undefined) {
      const a = this[0].get(name)
      const b = this[1].get(name)
      if (a === undefined && b === undefined) return []
      if (a === undefined) return [b]
      if (b === undefined) return [a]

      if (durationLessThanOrEqual(a.startTime, b.startTime)) {
        return [a, b]
      }
      return [b, a]
    } else if (type === 'mark') {
      const a = this[0].get(name)
      if (a === undefined) return []
      return [a]
    } else {
      const a = this[1].get(name)
      if (a === undefined) return []
      return [a]
    }
  }

  getMark (name) {
    return this[0].get(name)
  }

  setMark (name, startTime) {
    const mark = new DurationObserverMark(name, startTime)
    this[0].set(name, mark)
    return mark
  }

  getMeasure (name) {
    return this[1].get(name)
  }

  /**
   *
   * @param {string} name
   * @param {Duration} startTime
   * @param {Duration | DurationObserverMark | string} startMark
   * @param {Duration | DurationObserverMark | string} endMark
   * @returns {DurationObserverMeasure}
   */
  setMeasure (name, startMark, endMark) {
    startMark = Duration.isDuration(startMark)
      ? startMark
      : startMark instanceof DurationObserverMark
        ? startMark.startTime
        : this[0].get(startMark).startTime
    endMark = Duration.isDuration(endMark)
      ? endMark
      : endMark instanceof DurationObserverMark
        ? endMark.startTime
        : this[0].get(endMark).startTime

    if (endMark === undefined || startMark === undefined) {
      throw new RangeError('could not find marks')
    }

    const duration = endMark.subtract(startMark)
    const measure = new DurationObserverMeasure(name, startMark, duration)
    this[1].set(name, measure)
    return measure
  }
}

class DurationObserver {
  static #list = new DurationObserverList()

  static now () {
    const timeOrigin = SlotMap.getInternal(DurationObserver, 'timeOrigin')
    return DateTime.now().since(timeOrigin)
  }

  static mark (name) {
    return this.#list.setMark(name, DurationObserver.now())
  }

  static measure (name, startMark, endMark) {
    if (endMark === undefined) {
      endMark = DurationObserver.now()
    }

    return this.#list.setMeasure(name, startMark, endMark)
  }

  static getEntries () {
    return this.#list.entries()
  }

  static getEntriesByName (name, type) {
    return this.#list.entriesByName(name, type)
  }

  #listMap = new DurationObserverList()

  constructor () {
    const slotMap = SlotMap.get(this)
    slotMap.set('timeOrigin', DateTime.now())
  }

  now () {
    const timeOrigin = SlotMap.getInternal(this, 'timeOrigin')
    return DateTime.now().since(timeOrigin)
  }

  mark (name) {
    return this.#listMap.setMark(name, this.now())
  }

  measure (name, startMark, endMark) {
    if (endMark === undefined) {
      endMark = this.now()
    }

    return this.#listMap.setMeasure(name, startMark, endMark)
  }

  getEntries () {
    return this.#listMap.entries()
  }

  getEntriesByName (name, type) {
    return this.#listMap.entriesByName(name, type)
  }
}

SlotMap.setInternal(DurationObserver, 'timeOrigin', DateTime.now())

export default DurationObserver
